/* Stickiply cart panel controller.
   Sits on top of Dawn's cart.js / cart-drawer.js without replacing them:
   - anchors the popover under the header cart icon (desktop)
   - drives the bottom sheet (mobile) including drag to dismiss
   - Esc, outside click and focus-out close
   - quantity steppers (plain buttons dispatching `change` at Dawn's inputs)
   - the discount code field on /cart
   - the international weight-band shipping bar
   - aria-expanded on the discount / note disclosure rows
   - quick add from the "You might need" row
*/
(function () {
  'use strict';

  // stk i18n: window.stkT is defined by layout/theme.liquid before this file
  // loads; falls back to the English literal when the island/key is absent.
  var stkT = window.stkT || function (k, f) { return f; };
  function stkFmt(key, fallback, vars) {
    var s = stkT(key, fallback);
    if (vars) {
      for (var vk in vars) {
        if (Object.prototype.hasOwnProperty.call(vars, vk)) {
          s = s.split('{{' + vk + '}}').join(vars[vk]);
        }
      }
    }
    return s;
  }

  var DESKTOP = window.matchMedia('(min-width: 990px)');

  function drawer() {
    return document.querySelector('cart-drawer');
  }
  function panel() {
    return document.querySelector('cart-drawer .skc-panel');
  }
  function icon() {
    return document.getElementById('cart-icon-bubble');
  }
  function isOpen() {
    var d = drawer();
    return !!d && d.classList.contains('active');
  }

  /* ---------------------------------------------------------------- anchor */

  function positionPanel() {
    var b = icon();
    if (!b || !DESKTOP.matches) return;
    var r = b.getBoundingClientRect();
    var root = document.documentElement;
    root.style.setProperty('--skc-anchor-top', Math.round(r.bottom + 10) + 'px');
    root.style.setProperty('--skc-anchor-right', Math.max(12, Math.round(window.innerWidth - r.right)) + 'px');
  }

  /* ------------------------------------------------------- open/close hooks */

  function afterOpen() {
    var b = icon();
    if (b) b.setAttribute('aria-expanded', 'true');
    if (DESKTOP.matches) {
      // a popover must not lock the page; Dawn's drawer did
      document.body.classList.remove('overflow-hidden');
      document.documentElement.classList.remove('skc-lock');
    } else {
      document.documentElement.classList.add('skc-lock');
    }
  }

  function afterClose() {
    var b = icon();
    if (b) b.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('skc-lock');
    document.body.classList.remove('overflow-hidden');
    var p = panel();
    if (p) p.style.transform = '';
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer').then(function () {
      var P = customElements.get('cart-drawer').prototype;
      if (P.__skcPatched) return;
      P.__skcPatched = true;
      var open = P.open;
      var close = P.close;
      P.open = function (triggeredBy) {
        positionPanel();
        open.call(this, triggeredBy);
        afterOpen();
      };
      P.close = function () {
        close.call(this);
        afterClose();
      };
    });
  }

  /* --------------------------------------------------------------- closing */

  function closeCart() {
    var d = drawer();
    if (d && d.classList.contains('active')) d.close();
  }

  document.addEventListener(
    'keydown',
    function (e) {
      if (e.key === 'Escape' && isOpen()) {
        e.preventDefault();
        closeCart();
        var b = icon();
        if (b) b.focus();
      }
    },
    true
  );

  // toggle from the header icon (Dawn only ever opens)
  document.addEventListener(
    'click',
    function (e) {
      var b = icon();
      if (!b) return;
      if (!e.target.closest || !e.target.closest('#cart-icon-bubble')) return;
      if (isOpen()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeCart();
      }
    },
    true
  );

  // outside click (the overlay is click-through on desktop so no page dim is needed)
  document.addEventListener('pointerdown', function (e) {
    if (!isOpen() || !DESKTOP.matches) return;
    var t = e.target;
    if (t.closest && (t.closest('.skc-panel') || t.closest('#cart-icon-bubble'))) return;
    closeCart();
  });

  // focus leaving the panel
  document.addEventListener('focusin', function (e) {
    if (!isOpen() || !DESKTOP.matches) return;
    var t = e.target;
    if (t.closest && (t.closest('.skc-panel') || t.closest('#cart-icon-bubble'))) return;
    closeCart();
  });

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-skc-close]')) {
      e.preventDefault();
      closeCart();
    }
  });

  window.addEventListener('resize', function () {
    if (isOpen()) positionPanel();
    if (DESKTOP.matches) document.documentElement.classList.remove('skc-lock');
  });

  window.addEventListener(
    'scroll',
    function () {
      if (isOpen() && DESKTOP.matches) positionPanel();
    },
    { passive: true }
  );

  /* ------------------------------------------------- sheet: drag to dismiss */

  /* Pointer events, not touch events: the same handler then covers finger,
     trackpad and mouse, and it is testable. The drag may only begin on the grab
     handle or the sheet's head - never on the list, which has its own scroll. */

  var drag = null;
  var DRAG_FRACTION = 0.25; // of the sheet's height
  var DRAG_VELOCITY = 0.5; // px per ms, for a short flick
  var DRAG_MIN_FLICK = 40; // px, so a flick still has to travel

  /* Upward is a second gesture on the same grip: down continues shopping, up
     goes to the cart page. Thomas, 29 Aug: "could we make it so that you can
     swipe up on that drawer to open the cart page, and swipe down to continue
     shopping?"

     It is deliberately harder to trigger than the dismiss. Down is the safe
     direction, and a sheet that navigates on a small accidental upward twitch
     would take somebody off the page they were reading. So the distance is
     larger and the damping means the finger travels further than the sheet
     does.

     Nothing here is the only way to reach the cart page: the View cart button
     under it does the same thing, which is what keeps a gesture-only action
     from being a barrier. */
  var UP_TRAVEL = 90; // px of finger movement before it counts
  var UP_MAX_LIFT = 56; // px the sheet is allowed to rise, however far the finger goes
  var UP_DAMPING = 0.42; // sheet movement per px of finger, so it resists

  function sheetPanel() {
    return document.querySelector('cart-drawer .skc-panel');
  }

  document.addEventListener(
    'pointerdown',
    function (e) {
      if (DESKTOP.matches || !isOpen()) return;
      if (e.button != null && e.button !== 0) return;
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-skc-close]')) return; // the close button is not a grip
      var grip = t.closest('[data-skc-handle], .skc-panel__head');
      if (!grip) return;
      var p = sheetPanel();
      if (!p) return;
      drag = { y0: e.clientY, t0: Date.now(), p: p, id: e.pointerId, dy: 0, raw: 0, grip: grip };
      p.style.transition = 'none';
      try {
        grip.setPointerCapture(e.pointerId);
      } catch (err) {
        /* capture is a nicety, not a requirement */
      }
    },
    true
  );

  document.addEventListener(
    'pointermove',
    function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var raw = e.clientY - drag.y0;
      drag.raw = raw;

      /* Down follows the finger exactly, because the sheet really is leaving.
         Up is damped and capped, because it is not going anywhere: the lift is
         a signal that the gesture has been understood, not a preview of a
         taller sheet. */
      var shown = raw >= 0 ? raw : Math.max(-UP_MAX_LIFT, raw * UP_DAMPING);
      drag.dy = shown;
      drag.p.style.transform = 'translateY(' + shown + 'px)';
      if (Math.abs(raw) > 4 && e.cancelable) e.preventDefault();
    },
    { passive: false }
  );

  function endDrag() {
    if (!drag) return;
    var p = drag.p;
    var raw = drag.raw;
    var height = p.getBoundingClientRect().height || 1;
    var ms = Math.max(1, Date.now() - drag.t0);
    var speed = Math.abs(raw) / ms;

    var far = raw > height * DRAG_FRACTION;
    var flick = speed > DRAG_VELOCITY && raw > DRAG_MIN_FLICK;

    /* The upward test reads the RAW finger travel, not the damped movement of
       the sheet: the sheet only ever rises 56px, so judging it by where the
       sheet ended up would make the gesture impossible past that point. */
    var up = raw < -UP_TRAVEL || (speed > DRAG_VELOCITY && raw < -DRAG_MIN_FLICK);

    try {
      drag.grip.releasePointerCapture(drag.id);
    } catch (err) {
      /* nothing to release */
    }
    drag = null;

    p.style.transition = '';
    p.style.transform = '';

    if (up) {
      /* Let the sheet settle back before leaving, so the last thing seen is the
         panel returning rather than the page snapping out from under it. */
      setTimeout(function () {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }, 140);
      return;
    }

    if (far || flick) closeCart();
  }

  document.addEventListener('pointerup', endDrag, true);
  document.addEventListener('pointercancel', endDrag, true);

  /* ------------------------------------------------------ quantity steppers */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-skc-step]');
    if (!btn) return;
    e.preventDefault();
    var wrap = btn.closest('[data-skc-qty]');
    if (!wrap) return;
    var input = wrap.querySelector('input');
    if (!input || input.disabled) return;

    var step = Number(input.step) || 1;
    var min = input.min === '' ? 0 : Number(input.min);
    var max = input.max === '' || input.max == null ? Infinity : Number(input.max);
    var dir = Number(btn.getAttribute('data-skc-step'));
    var next = (Number(input.value) || 0) + dir * step;
    next = Math.max(min, Math.min(max, next));
    if (next === Number(input.value)) return;

    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------ action rows: aria-expanded    */

  // <summary> carries the label, so the expanded state has to be announced on it
  function syncRow(d) {
    var head = d.querySelector('.skc-row__head');
    if (head) head.setAttribute('aria-expanded', d.open ? 'true' : 'false');
  }

  document.addEventListener(
    'toggle',
    function (e) {
      var d = e.target;
      if (d && d.matches && d.matches('[data-skc-row]')) syncRow(d);
    },
    true
  );

  function syncRows() {
    var rows = document.querySelectorAll('[data-skc-row]');
    for (var i = 0; i < rows.length; i++) syncRow(rows[i]);
  }

  /* ------------------------------------------------ discount code (/cart)   */

  function cartJson() {
    return fetch('/cart.js', { cache: 'no-store', credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function appliedCodes(cart) {
    return (cart.discount_applications || [])
      .filter(function (d) {
        return d.type === 'discount_code' || d.title;
      })
      .map(function (d) {
        return String(d.title || '');
      });
  }

  /* THE APPLY PATH IS /cart/update.js, NOT THE /discount/ COOKIE.

     The original applied codes by fetching /discount/CODE and then looked for
     the code in cart.discount_applications. That check can never pass: the
     discount URL only sets a cookie that checkout reads later - it never
     touches the cart object - so EVERY code, valid or not, reported "did not
     apply". Found 31 Aug when Tash Minto's SHOWMETHEMONEY was refused;
     measured: her code applies fine through this endpoint (10% off,
     applicable: true), and a garbage code comes back applicable: false with
     the total untouched. The remove button below already used update.js,
     which is what gave the fix away.

     update.js REPLACES the cart's code, so a failed attempt also knocks off
     whatever good code was on - hence the previous code is captured first and
     put back on failure, same intent as before, now on a path that works. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest && e.target.closest('[data-skc-discount]');
    if (!form) return;
    e.preventDefault();

    var input = form.querySelector('input[name="discount"]');
    var msg = form.querySelector('.skc-discount__msg');
    var code = (input.value || '').trim();
    if (!code) return;

    msg.dataset.state = '';
    msg.textContent = stkT('stk.cart_lab.discount_checking', 'Checking that code…');

    function setDiscount(value) {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: value }),
      }).then(function (r) {
        return r.json();
      });
    }

    function applicableCode(cart) {
      var entry = (cart.discount_codes || []).filter(function (d) {
        return d.applicable;
      })[0];
      return entry ? entry.code : null;
    }

    cartJson()
      .then(function (before) {
        var previous = applicableCode(before);
        return setDiscount(code).then(function (cart) {
          var entry = (cart.discount_codes || []).filter(function (d) {
            return String(d.code).toUpperCase() === code.toUpperCase();
          })[0];

          if (entry && entry.applicable) {
            msg.dataset.state = 'ok';
            msg.textContent = stkT('stk.cart_lab.discount_applied', 'Code applied.');
            window.location.reload();
            return;
          }

          msg.dataset.state = 'error';
          msg.textContent = stkT('stk.cart_lab.discount_not_applicable', 'That code did not apply. Check it and try again, or enter it at checkout.');
          return setDiscount(previous || '');
        });
      })
      .catch(function () {
        msg.dataset.state = 'error';
        msg.textContent = stkT('stk.cart_lab.discount_check_failed', 'We could not check that code. Try it at checkout.');
      });
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-skc-discount-remove]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: '' }),
    })
      .catch(function () {})
      .then(function () {
        window.location.reload();
      });
  });

  /* ------------------------------------------------- quick add (/cart)       */

  // Refresh the parts of /cart that a cart change touches, without a navigation:
  // pull the page again and swap the four regions whose contents depend on the
  // cart. Cheaper to reason about than guessing section ids, and it keeps the
  // note and the discount row's open state, which live outside .js-contents.
  function refreshCartPage() {
    return fetch(window.location.pathname + window.location.search, {
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        ['#main-cart-items', '#main-cart-footer', '.skc-upsell', '#cart-icon-bubble'].forEach(function (sel) {
          var a = document.querySelector(sel);
          var b = doc.querySelector(sel);
          if (a && b) a.innerHTML = b.innerHTML;
        });

        // the panel is rendered by the layout, so it does not come along with the
        // page sections; refresh its contents too or it goes stale behind the page.
        // Only .drawer__inner is replaced: #CartDrawer-Overlay is a sibling and
        // Dawn's cart-drawer bound its click handler to that node.
        var di = document.querySelector('cart-drawer .drawer__inner');
        var dj = doc.querySelector('cart-drawer .drawer__inner');
        if (di && dj) {
          di.innerHTML = dj.innerHTML;
          di.className = dj.className;
        }
        var cd = document.querySelector('cart-drawer');
        var cdNew = doc.querySelector('cart-drawer');
        if (cd && cdNew) cd.classList.toggle('is-empty', cdNew.classList.contains('is-empty'));

        syncRows();
        fillWeightBars();
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-skc-quickadd]');
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;

    // a multi-variant card carries a select; a single-variant one carries the id
    var wrap = btn.closest('.skc-upsell__pick') || btn.parentNode;
    var select = wrap ? wrap.querySelector('[data-skc-variant-select]') : null;
    var id = Number(select ? select.value : btn.getAttribute('data-variant-id'));
    var label = btn.querySelector('span');
    if (!id) return;

    btn.setAttribute('aria-busy', 'true');
    if (label) label.textContent = stkT('stk.cart_lab.mini_add_adding', 'Adding');

    fetch(((window.Shopify&&window.Shopify.routes&&window.Shopify.routes.root)||"/") + 'cart/add.js', {  // P172: product titles in the page language
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] }),
    })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) {
            var addErr = new Error(body.description || 'add failed');
            addErr.status = r.status;
            throw addErr;
          }
          return body;
        });
      })
      .then(refreshCartPage)
      .then(function () {
        document.dispatchEvent(new CustomEvent('skc:cart-updated'));
      })
      .catch(function (err) {
        btn.removeAttribute('aria-busy');
        // English behaviour is frozen in this pass. Status alone is not a substitute:
        // an over-quantity add is also a 422, with the description "All N X are in your
        // cart", which shows "Try again" today and would become "Sold out". Shopify gives
        // no non-text signal that separates the two, so the localised description stays
        // the discriminator and this remains language-dependent. P4 follow-up.
        var msg = String((err && err.message) || '');
        if (label) label.textContent = /sold out|stock|available/i.test(msg)
          ? stkT('stk.cart_lab.mini_add_sold_out', 'Sold out')
          : stkT('stk.cart_lab.mini_add_try_again', 'Try again');
      });
  });

  /* ---------------------------------------------- international weight bands */

  var bandsPromise = null;

  function loadBands(url) {
    var override = document.querySelector('script[data-skc-bands-override]');
    if (override) {
      try {
        return Promise.resolve(JSON.parse(override.textContent));
      } catch (err) {
        /* fall through to the asset */
      }
    }
    if (!bandsPromise) {
      bandsPromise = fetch(url, { cache: 'force-cache' })
        .then(function (r) {
          return r.json();
        })
        .catch(function () {
          return null;
        });
    }
    return bandsPromise;
  }

  // Countries that read parcel weights in ounces and pounds. Everywhere else we
  // ship to is metric, so this is the whole exception list - add an ISO code
  // here (e.g. 'CA') if a market turns out to prefer imperial.
  var IMPERIAL = ['US'];

  function trim(n, dp) {
    return n.toFixed(dp).replace(/\.?0+$/, '');
  }

  // Shopify holds cart weights in grams; only the display changes per country.
  /* P172 (2 Oct 2026): the parcel sentences now read in the page language, so the weight's decimal mark
     follows it ("1,25 kg" on /de /fr ...); English keeps the point, exactly as before */
  var WEIGHT_DEC = (function () {
    try {
      var l = document.documentElement.lang || 'en';
      return /^en/i.test(l) ? '.' : ((1.5).toLocaleString(l).replace(/[0-9]/g, '') || '.');
    } catch (e) { return '.'; }
  })();
  function fmtWeight(g, code) {
    if (IMPERIAL.indexOf((code || '').toUpperCase()) !== -1) {
      var oz = g / 28.349523125;
      if (oz < 16) return trim(Math.round(oz * 10) / 10, 1).replace('.', WEIGHT_DEC) + ' oz';
      return trim(Math.round((oz / 16) * 100) / 100, 2).replace('.', WEIGHT_DEC) + ' lb';
    }
    if (g < 1000) return Math.round(g) + ' g';
    return trim(Math.round(g / 10) / 100, 2).replace('.', WEIGHT_DEC) + ' kg';
  }

  function fmtMoneyGBP(gbp) {
    var rate = 1;
    var cur = 'GBP';
    try {
      if (window.Shopify && Shopify.currency) {
        rate = Number(Shopify.currency.rate) || 1;
        cur = Shopify.currency.active || 'GBP';
      }
    } catch (err) {
      /* defaults */
    }
    var v = gbp * rate;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: cur,
      }).format(v);
    } catch (err) {
      return v.toFixed(2) + ' ' + cur;
    }
  }

  // the bar's message is two spans and a rule, matching the Liquid the free
  // shipping bar renders, so both modes read as one component
  function setMsg(el, key, val) {
    // the delivery glyph is rendered by the Liquid; keep it and rebuild the rest
    var icon = el.querySelector('svg');
    el.textContent = '';
    if (icon) el.appendChild(icon);
    var k = document.createElement('span');
    k.className = 'skc-ship__key';
    k.textContent = key;
    var sep = document.createElement('span');
    sep.className = 'skc-ship__sep';
    sep.setAttribute('aria-hidden', 'true');
    sep.textContent = '|';
    var v = document.createElement('span');
    v.className = 'skc-ship__val';
    v.textContent = val;
    el.appendChild(k);
    el.appendChild(sep);
    el.appendChild(v);
  }

  /* ------------------------------------------- upsell that fits the band

     "You might need" should only offer things the customer can actually add
     without tipping the parcel into the next (dearer) shipping band. Weights
     come from the variant itself, so the row re-reads on every cart change.

     upsellRoom is the grams left before the next cap: null means there is no
     next cap to protect (UK free shipping, or a cart already in the top band),
     and then nothing is filtered.                                          */

  var upsellRoom = null;
  var upsellCountry = '';

  // the row means something different once it has been filtered, so it says so
  function setUpsellTitle(filtered) {
    var h = document.querySelector('.skc-upsell__title');
    if (!h) return;
    var base = h.getAttribute('data-skc-title-base');
    var fit = h.getAttribute('data-skc-title-fit');
    if (!base) return;
    h.textContent =
      filtered && fit ? fit.replace('{room}', fmtWeight(upsellRoom, upsellCountry)) : base;
  }

  // heaviest option that still fits, so we downsize the pick as little as
  // possible; returns false only when no option fits at all
  function fitCard(card, room) {
    var sel = card.querySelector('[data-skc-variant-select]');
    if (!sel) {
      var w = Number(card.getAttribute('data-skc-weight')) || 0;
      return w <= room;
    }
    var cur = sel.options[sel.selectedIndex];
    if (cur && (Number(cur.getAttribute('data-weight')) || 0) <= room) return true;
    var best = null;
    var bestW = -1;
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      if (o.disabled) continue;
      var ow = Number(o.getAttribute('data-weight')) || 0;
      if (ow <= room && ow > bestW) {
        best = o;
        bestW = ow;
      }
    }
    if (best) {
      sel.value = best.value;
      return true;
    }
    return false;
  }

  function applyUpsellFit() {
    var grid = document.querySelector('.skc-upsell__grid');
    if (!grid) return;
    var cards = [].slice.call(grid.querySelectorAll('.skc-upsell__card'));
    if (!cards.length) return;

    if (upsellRoom === null) {
      cards.forEach(function (c) {
        c.hidden = false;
      });
      setUpsellTitle(false);
      return;
    }

    var fits = cards.filter(function (c) {
      return fitCard(c, upsellRoom);
    });

    // fitting first, so the row still reads well when nothing is hidden
    fits.forEach(function (c) {
      grid.appendChild(c);
    });
    cards.forEach(function (c) {
      if (fits.indexOf(c) === -1) grid.appendChild(c);
    });

    // never empty the row to make a point: below two survivors we show them all
    var hide = fits.length >= 2;
    cards.forEach(function (c) {
      c.hidden = hide && fits.indexOf(c) === -1;
    });
    setUpsellTitle(hide && fits.length < cards.length);
  }

  function fillWeightBars() {
    if (!document.querySelector('[data-skc-ship="weight"]')) {
      upsellRoom = null;
      applyUpsellFit();
    }

    var els = document.querySelectorAll('[data-skc-ship="weight"]:not([data-skc-done])');
    if (!els.length) return;

    els.forEach(function (el) {
      el.setAttribute('data-skc-done', '1');
      var url = el.getAttribute('data-bands-url');
      var code = (el.getAttribute('data-country') || '').toUpperCase();
      var name = el.getAttribute('data-country-name') || code;
      var weight = Number(el.getAttribute('data-weight')) || 0;

      var dutyDap = el.getAttribute('data-duty-dap') || '';
      var dutyDdp = el.getAttribute('data-duty-ddp') || '';

      loadBands(url).then(function (doc) {
        if (!doc || !doc.countries) return;
        var zone = doc.countries[code];
        if (!zone || !zone.b || !zone.b.length) return;

        var band = null;
        var prevCap = 0;
        for (var i = 0; i < zone.b.length; i++) {
          var cap = zone.b[i][0];
          if (cap === null || weight <= cap) {
            band = { cap: cap, price: zone.b[i][1] };
            break;
          }
          prevCap = cap;
        }
        if (!band) band = { cap: null, price: zone.b[zone.b.length - 1][1] };

        var msg = el.querySelector('.skc-ship__msg');
        var note = el.querySelector('.skc-ship__note');
        var fill = el.querySelector('.skc-ship__fill');
        var track = el.querySelector('.skc-ship__track');

        // same shape as the free shipping bar: a label, a rule, a figure
        var prefix = el.getAttribute('data-prefix') || stkT('stk.cart_lab.shipping_to_prefix_default', 'Shipping to');
        var price = fmtMoneyGBP(band.price);
        setMsg(msg, prefix + ' ' + name, price);

        var detail;
        if (band.cap === null) {
          detail = stkFmt('stk.cart_lab.parcel_is_prefix', 'Your parcel is {{weight}}.', { weight: fmtWeight(weight, code) });
          upsellRoom = null;   // top band: there is no next threshold to stay under
          applyUpsellFit();
          fill.style.setProperty('--skc-pct', '100%');
          el.classList.add('is-done');
          if (track) track.setAttribute('aria-valuenow', '100');
        } else {
          var room = Math.max(0, band.cap - weight);
          var span = band.cap - prevCap;
          var pct = span > 0 ? Math.min(100, Math.round(((weight - prevCap) / span) * 100)) : 0;
          // a cart sitting exactly on the cap has no room left; "add 0 oz" is not a
          // sentence worth printing, so the weight stands on its own
          var roomTxt = fmtWeight(room, code);
          detail = stkFmt('stk.cart_lab.parcel_is_prefix', 'Your parcel is {{weight}}.', { weight: fmtWeight(weight, code) });
          if (room > 0 && !/^0\s/.test(roomTxt)) {
            detail += stkFmt('stk.cart_lab.parcel_room_note', ' You can add {{room}} before the next band.', { room: roomTxt });
          }
          upsellRoom = room;
          upsellCountry = code;
          applyUpsellFit();
          fill.style.setProperty('--skc-pct', Math.max(2, pct) + '%');
          if (track) track.setAttribute('aria-valuenow', String(pct));
        }

        // Thomas, 23 Aug: the note is the weight and nothing else - the price is
        // already on the line above, and the duty and checkout sentences were noise
        if (note) note.textContent = detail;
        el.hidden = false;
      });
    });
  }

  /* ---------------------------------------------------------------- boot up */

  function boot() {
    /* P208: positioning reads the cart icon's layout; open() does it right before the panel shows,
       so on load it is only needed when the panel starts open (11 ms forced layout otherwise) */
    if (isOpen()) positionPanel();
    fillWeightBars();
    syncRows();
    var b = icon();
    if (b && !b.hasAttribute('aria-expanded')) b.setAttribute('aria-expanded', String(isOpen()));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Dawn swaps section HTML in on every cart change; re-run against the new nodes.
  var t = null;
  new MutationObserver(function () {
    clearTimeout(t);
    t = setTimeout(function () {
      fillWeightBars();
      syncRows();
      if (isOpen()) positionPanel();
    }, 120);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* ===========================================================================
   Overlay manager - only one overlay on screen at a time     (22 Aug 2026)

   Thomas's screenshot had the mega menu and the cart panel open together.
   They must be mutually exclusive, and so must the search panel, the account
   sheet and the mobile drawer.

   Written as a REGISTRY rather than as one-off handlers wired into each
   component: every overlay declares how to report that it is open and how to
   shut itself, and whenever any one of them opens the rest are told to close.
   Adding a sixth overlay later means adding one entry here, nothing else.

   It OBSERVES state instead of requiring hooks inside each component. That is
   deliberate: the mega menu's own script (snippets/header-mega-menu.liquid),
   Dawn's details-modal and the account sheet are all owned elsewhere, and
   several are being edited concurrently. Watching class / open / aria-expanded
   changes means this file is the only one that has to know the rule.

   The menu is closed by clicking its own toggle so its script keeps its
   internal state in step; stripping the class is only the fallback for when
   the button has been re-rendered away.
   =========================================================================== */

(function () {
  'use strict';

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }
  function q(sel) {
    return document.querySelectorAll(sel);
  }

  var OVERLAYS = [
    {
      name: 'cart',
      isOpen: function () {
        var d = document.querySelector('cart-drawer');
        return !!(d && d.classList.contains('active'));
      },
      close: function () {
        var d = document.querySelector('cart-drawer');
        if (d && typeof d.close === 'function') {
          try { d.close(); } catch (e) { d.classList.remove('active'); }
        }
      }
    },
    {
      name: 'search',
      isOpen: function () {
        /* the desktop search field is held open on purpose (header.liquid sets
           html.stk-search-held from 990px, 6 Sep 2026): not an overlay, never
           closed, or every menu hover shut it and Dawn's close() moved focus to
           the magnifier, which shut the menu in turn */
        if (document.documentElement.classList.contains('stk-search-held')) return false;
        return !!document.querySelector('details-modal.header__search details[open]');
      },
      close: function () {
        if (document.documentElement.classList.contains('stk-search-held')) return;
        each(q('details-modal.header__search details[open]'), function (d) {
          var m = d.parentElement;
          if (m && typeof m.close === 'function') {
            try { m.close(); return; } catch (e) {}
          }
          d.removeAttribute('open');
        });
      }
    },
    {
      name: 'menu',
      isOpen: function () {
        return !!document.querySelector('.mm-item.is-open');
      },
      close: function () {
        each(q('.mm-item.is-open .mm__toggle, .mm__toggle[aria-expanded="true"]'), function (b) {
          try { b.click(); } catch (e) {}
        });
        each(q('.mm-item.is-open'), function (i) {
          i.classList.remove('is-open');
          var t = i.querySelector('.mm__toggle');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
    },
    {
      name: 'drawer',
      isOpen: function () {
        return !!document.querySelector('.menu-drawer-container details[open]');
      },
      close: function () {
        each(q('.menu-drawer-container details[open]'), function (d) {
          var m = d.parentElement;
          if (m && typeof m.close === 'function') {
            try { m.close(); return; } catch (e) {}
          }
          d.removeAttribute('open');
        });
      }
    },
    {
      name: 'account',
      isOpen: function () {
        var a = document.querySelector('shopify-account, .header__icon--account[aria-expanded="true"]');
        if (!a) return false;
        return a.hasAttribute('open') || a.classList.contains('is-open') ||
               a.getAttribute('aria-expanded') === 'true';
      },
      close: function () {
        var a = document.querySelector('shopify-account');
        if (a && typeof a.close === 'function') {
          try { a.close(); } catch (e) {}
        }
        each(q('.header__icon--account[aria-expanded="true"]'), function (b) {
          b.setAttribute('aria-expanded', 'false');
        });
      }
    }
  ];

  var busy = false;
  var last = {};

  function snapshot() {
    var s = {};
    for (var i = 0; i < OVERLAYS.length; i++) s[OVERLAYS[i].name] = OVERLAYS[i].isOpen();
    return s;
  }

  function closeOthers(keep) {
    busy = true;
    for (var i = 0; i < OVERLAYS.length; i++) {
      var o = OVERLAYS[i];
      if (o.name === keep) continue;
      if (o.isOpen()) {
        try { o.close(); } catch (e) {}
      }
    }
    /* let the closes land, then re-baseline so the shut overlays are not read
       as a fresh open on the next tick */
    setTimeout(function () {
      last = snapshot();
      busy = false;
    }, 40);
  }

  function scan() {
    if (busy) return;
    var now = snapshot();
    var opened = null;
    for (var i = 0; i < OVERLAYS.length; i++) {
      var n = OVERLAYS[i].name;
      if (now[n] && !last[n]) { opened = n; break; }
    }
    last = now;
    if (opened) closeOthers(opened);
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      scan();
    });
  }

  function start() {
    last = snapshot();
    new MutationObserver(schedule).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'open', 'aria-expanded']
    });
    /* a click can open something without any attribute landing before the next
       frame, so give the pointer a look-in too */
    document.addEventListener('click', schedule, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* ===========================================================================
   CART ROUND 7 - the mobile add-to-cart toast                  (22 Aug 2026)

   On a phone, adding something no longer throws the half-screen sheet up. A
   compact card lands under the header cart icon, names what went in, and goes
   away by itself. Tapping it opens the sheet.

   WHERE IT HOOKS IN. Every add-to-cart in this theme ends at
   CartDrawer.renderContents(), which re-renders the cart sections and then
   calls open():
     - PDP                 product-form.js  -> this.cart.renderContents(res)
     - quick add           product-form.js inside quick-add-modal, same call
     - Bookmark / Proof /
       Sticker Sheet Lab   lab-cart.js      -> drawer.renderContents(addRes)
   So renderContents is the one place worth intercepting: the sections still
   render (the header count, the sheet's contents), only the open() is swapped
   for the toast. Dawn's open() is left alone in every other case, which is why
   the header icon, the toast itself and desktop all still open the panel.

   The upsell row on /cart adds through its own fetch (see the [data-skc-quickadd]
   handler above) and never touches the drawer, so it is picked up separately
   from the skc:cart-updated event it already dispatches.

   Not stacked: a second add replaces the card's contents and restarts the
   clock, so a customer tapping add three times fast sees one card naming the
   last thing added, not a pile.
   =========================================================================== */
(function () {
  'use strict';

  // stk i18n: window.stkT is defined by layout/theme.liquid before this file
  // loads; falls back to the English literal when the island/key is absent.
  var stkT = window.stkT || function (k, f) { return f; };
  function stkFmt(key, fallback, vars) {
    var s = stkT(key, fallback);
    if (vars) {
      for (var vk in vars) {
        if (Object.prototype.hasOwnProperty.call(vars, vk)) {
          s = s.split('{{' + vk + '}}').join(vars[vk]);
        }
      }
    }
    return s;
  }

  var DESKTOP = window.matchMedia('(min-width: 990px)');
  /* Overridden per shop from the header's settings; see iconEl()'s data
     attributes. The literal stays as the fallback for a page without a cart
     icon, which is only the checkout. */
  var DWELL = 2500; // ms on screen

  function toastOpt(name, fallback) {
    var el = document.getElementById('cart-icon-bubble');
    if (!el || !el.hasAttribute(name)) return fallback;
    var raw = el.getAttribute(name);
    var n = parseInt(raw, 10);
    return isNaN(n) ? raw : n;
  }
  var FADE = 200; // ms, matches the CSS transition

  var card = null;
  var live = null;
  var thumbEl = null;
  var nameEl = null;
  var dwellTimer = null;
  var fadeTimer = null;

  /* set immediately before Dawn's renderContents runs and consumed by the very
     next open(); the timer is only a belt-and-braces reset for the case where
     open() never fires at all */
  var swallowOpen = false;
  var swallowTimer = null;

  function drawer() {
    return document.querySelector('cart-drawer');
  }
  function iconEl() {
    return document.getElementById('cart-icon-bubble');
  }

  function build() {
    if (card) return;

    live = document.createElement('p');
    live.className = 'skc-toast-live';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');

    card = document.createElement('button');
    card.type = 'button';
    card.className = 'skc-toast';
    card.hidden = true;
    card.innerHTML =
      /* A drawn plus, not a typed one. The house rule is a 400 weight ceiling
         because Sapiens ships only a 400 and anything heavier is synthetic; a
         stroke width on a glyph is neither of those things, so this can be as
         heavy as it needs to be. */
      '<svg class="skc-toast__plus" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="3.4" stroke-linecap="round" aria-hidden="true" focusable="false">' +
      '<path d="M12 5v14M5 12h14"/></svg>' +
      '<img class="skc-toast__thumb" alt="" width="40" height="40" hidden>' +
      '<span class="skc-toast__txt">' +
      '<span class="skc-toast__kicker">' + stkT('stk.cart_lab.toast_kicker_added', 'Added') + '</span>' +
      '<span class="skc-toast__name"></span>' +
      '</span>' +
      /* The two facts that actually changed. They pressed the button, so they
         know what they added; what they do not know is what it came to and how
         many went in. Both are already in the /cart/add.js body this is built
         from, so they cost nothing to show. */
      '<span class="skc-toast__total" hidden>' +
      '<span class="skc-toast__prices"><s class="skc-toast__was" hidden></s>' +
      '<span class="skc-toast__price"></span></span>' +
      '<span class="skc-toast__qty" hidden></span>' +
      '</span>';

    document.body.appendChild(live);
    document.body.appendChild(card);

    thumbEl = card.querySelector('.skc-toast__thumb');
    nameEl = card.querySelector('.skc-toast__name');

    /* It leaves after 2.5s whether or not somebody is reading it. On a desktop,
       where it is a button under the cursor's own corner of the screen, that is
       long enough to start reaching for and not long enough to arrive. */
    card.addEventListener('mouseenter', function () {
      clearTimeout(dwellTimer);
      clearTimeout(fadeTimer);
    });
    card.addEventListener('mouseleave', function () {
      clearTimeout(dwellTimer);
      dwellTimer = setTimeout(function () {
        hide();
      }, DWELL);
    });

    card.addEventListener('click', function (e) {
      e.preventDefault();
      openSheet();
    });
  }

  /* under the cart icon, right-aligned to it, exactly like the desktop popover
     anchors itself. The header is NOT sticky on a phone, so once the page has
     been scrolled the icon is above the viewport and its rect is negative -
     hence the floor: the card then pins near the top right instead of being
     placed off the top of the screen. */
  function place() {
    var b = iconEl();
    var top = 68;
    var right = 12;
    if (b) {
      var r = b.getBoundingClientRect();
      if (r.width) {
        top = Math.round(r.bottom + 8);
        right = Math.max(10, Math.round(window.innerWidth - r.right));
      }
    }
    card.style.top = Math.max(14, top) + 'px';
    /* Centred on a phone, where there is no room to sit beside anything and the
       icon it points at has usually scrolled away anyway. The inline right is
       cleared rather than overwritten, so the stylesheet's centring is not
       fighting an inline value it can never beat. */
    if (window.matchMedia('(max-width: 749px)').matches) {
      card.style.removeProperty('right');
    } else {
      card.style.right = right + 'px';
    }
  }

  /* /cart/add.js answers with the line item for a single add and with
     { items: [...] } for a multi-line add; the Labs use the second shape.
     P240 review-2 MED 2 (1 Nov 2026): an add with the artwork check also carries
     its check line (properties._check_for), placed after the design line. The
     card names the design: the last line WITHOUT _check_for. A product-form add
     can answer with the check line alone; then the design line is read from the
     cart by the pairing token (designFor). */
  function isCheckLine(it) {
    return !!(it && it.properties && it.properties._check_for);
  }
  function lineFrom(res) {
    if (!res || typeof res !== 'object') return null;
    if (Array.isArray(res.items) && res.items.length) {
      for (var i = res.items.length - 1; i >= 0; i--) {
        if (!isCheckLine(res.items[i])) return res.items[i];
      }
      return null;
    }
    if (res.product_title || res.title) return res;
    return null;
  }
  function designFor(check) {
    var tok = check.properties._check_for;
    var url = ((window.routes && window.routes.cart_url) || '/cart') + '.js';
    return fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) {
        return r.json();
      })
      .then(function (c) {
        return (
          ((c && c.items) || []).filter(function (it) {
            return it && it.properties && it.properties._check_id === tok;
          })[0] || null
        );
      })
      .catch(function () {
        return null;
      });
  }

  /* 26 Sep 2026, Thomas: "when i add to cart, its showing that hidden
     product". A custom-size job rides the hidden carrier product (id below, the
     same one the cart transform checks), and /cart/add.js names it "Custom Vinyl
     Stickers (Made to Size)" with no image. The toast shows the catalogue
     product instead. Keyed on the product id, never on a title. The name and
     image come from the carrier line cart-line-lab.liquid has just rendered into
     the drawer (localised, the catalogue's featured image); the constants are
     the fallback when no drawer line is on the page. */
  var CARRIER_PRODUCT_ID = 15907556819327;
  var CARRIER_FALLBACK = {
    name: 'Custom Vinyl Stickers',
    img: 'https://cdn.shopify.com/s/files/1/0809/5219/7414/files/custom-vinyl-stickers-262117.jpg?width=96'
  };

  function carrierDisplay(line) {
    if (!line || Number(line.product_id) !== CARRIER_PRODUCT_ID) return null;
    var el = document.querySelector('[data-stk-display-name]');
    var name = el ? el.getAttribute('data-stk-display-name') : '';
    var img = el ? el.getAttribute('data-stk-display-img') : '';
    return {
      name: name || stkT('stk.cart_lab.carrier_name_fallback', CARRIER_FALLBACK.name),   /* P172 (2 Oct 2026): the product's own translated title */
      img: img || CARRIER_FALLBACK.img
    };
  }

  function thumbUrl(url) {
    if (!url || typeof url !== 'string') return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=96';
  }

  function hide(now) {
    clearTimeout(dwellTimer);
    clearTimeout(fadeTimer);
    if (!card || card.hidden) return;
    card.classList.remove('is-in');
    if (now) {
      card.hidden = true;
    } else {
      fadeTimer = setTimeout(function () {
        card.hidden = true;
      }, FADE);
    }
  }

  function show(line) {
    /* Was desktop-excluded while the popover still opened on an add. It no
       longer does, at any width, so this is now the only thing on screen that
       says the add worked. 29 Aug. */
    build();

    var disp = carrierDisplay(line);
    var name = disp ? disp.name : (line && (line.product_title || line.title)) || '';
    var img = disp ? '' : (line && (line.image || (line.featured_image && line.featured_image.url))) || '';
    var qty = line && typeof line.quantity === 'number' ? line.quantity : 0;
    var now = line && typeof line.final_line_price === 'number' ? line.final_line_price : null;
    var was = line && typeof line.original_line_price === 'number' ? line.original_line_price : null;
    /* P128b (5 Oct 2026, Thomas: "the cart notification still shows the discount"): a custom-quantity line's
       "Adjustment" (the cq-round rounding pennies) is not a saving, so it is taken out of the struck price.
       Same titles as snippets/stk-adj-titles.liquid (keep the two in step). */
    var ADJ = '|Adjustment|Preisanpassung|Ajustement|Ajuste|Adeguamento|Prijsaanpassing|Justering|Pyöristys|Custom quantity|Individuelle Menge|Quantité personnalisée|Cantidad personalizada|Quantità personalizzata|Eigen aantal|Valfritt antal|Valgfrit antal|Oma määrä|';
    if (was !== null && line && Array.isArray(line.line_level_discount_allocations)) {
      line.line_level_discount_allocations.forEach(function (d) {
        var t = d && d.discount_application && d.discount_application.title;
        if (t && ADJ.indexOf('|' + t + '|') >= 0 && typeof d.amount === 'number') was -= d.amount;
      });
    }

    nameEl.textContent = name;

    /* Presentment currency. /cart/add.js already answers in the currency the
       customer is being shown, so this must NOT go through the rate conversion
       the shipping bar does: that one starts from shop currency and would
       convert a second time. */
    var money = function (cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP'
        }).format(cents / 100);
      } catch (e) {
        return '';
      }
    };

    /* String(), because toastOpt returns a NUMBER when the attribute parses as
       one, and "1" does. Comparing that against the string '1' was false every
       time, so the price and the quantity were switched off on every add by the
       very check meant to switch them on. */
    DWELL = toastOpt('data-skc-toast-dwell', 2500);
    if (String(toastOpt('data-skc-toast-price', 1)) !== '1') now = null;
    if (String(toastOpt('data-skc-toast-qty', 1)) !== '1') qty = 0;

    var totalEl = card.querySelector('.skc-toast__total');
    var priceEl = card.querySelector('.skc-toast__price');
    var wasEl = card.querySelector('.skc-toast__was');
    var qtyEl = card.querySelector('.skc-toast__qty');
    var priceText = now === null ? '' : money(now);

    priceEl.textContent = priceText;
    totalEl.hidden = !priceText;

    var showWas = was !== null && now !== null && was > now;
    wasEl.textContent = showWas ? money(was) : '';
    wasEl.hidden = !showWas;

    /* "Qty 1" on every add is noise; the only interesting quantity is one that
       is not one. */
    qtyEl.textContent = qty > 1 ? stkFmt('stk.cart_lab.toast_qty_prefix', 'Qty {{qty}}', { qty: qty }) : '';
    qtyEl.hidden = qty <= 1;
    if (disp && disp.img) {
      thumbEl.src = disp.img; /* already sized */
      thumbEl.hidden = false;
    } else if (img) {
      thumbEl.src = thumbUrl(img);
      thumbEl.hidden = false;
    } else {
      thumbEl.removeAttribute('src');
      thumbEl.hidden = true;
    }
    /* The figures are never visual only: whatever the card shows, the label and
       the live region say. */
    var spoken = name
      ? stkFmt('stk.cart_lab.toast_live_added_named', 'Added to cart: {{name}}', { name: name })
      : stkT('stk.cart_lab.toast_spoken_added', 'Added to cart');
    if (qty > 1) spoken += stkFmt('stk.cart_lab.toast_spoken_qty', ', quantity {{qty}}', { qty: qty });
    if (priceText) spoken += ', ' + priceText;
    card.setAttribute('aria-label', spoken + stkT('stk.cart_lab.toast_spoken_open_cart_suffix', '. Open cart'));

    /* replace, never stack: a second add reuses this one card and restarts the
       clock, so rapid adds leave one card naming the latest item */
    clearTimeout(dwellTimer);
    clearTimeout(fadeTimer);
    card.hidden = false;
    place();
    card.offsetWidth; // force a frame so the transition runs from the start state
    card.classList.add('is-in');

    /* re-announced on every add because the text is rewritten each time */
    live.textContent = name
      ? stkFmt('stk.cart_lab.toast_live_added_named', 'Added to cart: {{name}}', { name: name })
      : stkT('stk.cart_lab.toast_spoken_added', 'Added to cart');

    dwellTimer = setTimeout(function () {
      hide(false);
    }, DWELL);
  }

  function openSheet() {
    var d = drawer();
    hide(true);
    if (!d || typeof d.open !== 'function') {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      return;
    }
    d.open(iconEl() || undefined);
  }

  /* ------------------------------------------------------------- the hook */

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer').then(function () {
      var P = customElements.get('cart-drawer').prototype;
      if (P.__skcToast7) return;
      P.__skcToast7 = true;

      var render = P.renderContents;
      var open = P.open;

      P.renderContents = function (parsedState) {
        /* Never open the panel on an add, on any width. It was phones only
           until 29 Aug, when Thomas pointed out the desktop popover lands on
           top of the variant options he had just been using: "when i add to
           cart on desktop the cart opens and covers my options. maybe if
           should stay closed since the animation will lead people to click the
           cart icon anyway."

           If the panel is ALREADY open the add belongs in it, so that case is
           still excluded and nothing is swallowed. */
        var toast = !this.classList.contains('active');
        if (toast) {
          swallowOpen = true;
          clearTimeout(swallowTimer);
          swallowTimer = setTimeout(function () {
            swallowOpen = false;
          }, 800);
        }
        render.call(this, parsedState);
        if (toast) {
          var ln = lineFrom(parsedState);
          if (isCheckLine(ln)) designFor(ln).then(show);
          else show(ln);
        }
      };

      /* renderContents schedules its open() in a setTimeout, so the flag is
         still standing when this runs */
      P.open = function () {
        if (swallowOpen) {
          swallowOpen = false;
          clearTimeout(swallowTimer);
          return;
        }
        hide(true);
        return open.apply(this, arguments);
      };
    });
  }

  /* the /cart upsell row: its own fetch, its own event. The card the button
     sits in carries the name and the image, so the toast can be built from the
     DOM rather than from a response body. */
  var pending = null;

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('[data-skc-quickadd]');
      if (!btn || DESKTOP.matches) return;
      var cardEl = btn.closest('.skc-upsell__card');
      if (!cardEl) return;
      var n = cardEl.querySelector('.skc-upsell__name');
      var im = cardEl.querySelector('img');
      pending = { product_title: n ? n.textContent.trim() : '', image: im ? im.currentSrc || im.src : '' };
    },
    true
  );

  document.addEventListener('skc:cart-updated', function () {
    if (DESKTOP.matches || !pending) return;
    show(pending);
    pending = null;
  });

  /* an orientation change or a rotate while the card is up would leave it
     anchored to where the icon used to be */
  window.addEventListener('resize', function () {
    if (card && !card.hidden) {
      if (DESKTOP.matches) hide(true);
      else place();
    }
  });

  document.addEventListener(
    'keydown',
    function (e) {
      if (e.key === 'Escape' && card && !card.hidden) hide(true);
    },
    true
  );
})();
